import React from "react";
import { Link } from "react-router-dom";
import {
  Crown, Star, MapPin, BadgeCheck, ArrowRight, Sparkles, Check,
  TrendingUp, Users, Zap, Award, Clock4,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { PROVIDERS, HERO_IMAGES } from "../lib/mockData";

const PREMIUM = PROVIDERS.filter(p => p.premium);

const PREMIUM_BENEFITS = [
  { icon: TrendingUp, title: "4× more qualified leads",   desc: "Premium listings appear above standard results — across search, compare, and home." },
  { icon: Users,      title: "Featured profile page",      desc: "Showcase your senior team, past patents, case studies, and credentials at scale." },
  { icon: Sparkles,   title: "Editorial spotlight",        desc: "One-of-a-kind editorial profiles with custom imagery, sample drafts, and partner bios." },
  { icon: Award,      title: "Verified Gold badge",        desc: "Trust signal across every touchpoint — search, comparison, and innovator inboxes." },
  { icon: Zap,        title: "Priority lead routing",      desc: "High-intent briefs routed to you first, with first-rights-of-refusal for 60 minutes." },
  { icon: Clock4,     title: "Concierge onboarding",       desc: "Dedicated Udhyak account manager helps optimise your profile for conversion." },
];

export default function AttorneysPremium() {
  return (
    <div className="surface-cream min-h-screen" data-testid="premium-page">
      <Header/>

      {/* Premium Hero */}
      <section className="relative overflow-hidden bg-[#0A2520] text-white">
        <div className="absolute inset-0 grid-bg opacity-20"/>
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] blob bg-[#F5D442]"/>
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] blob bg-[#0E6E5E]"/>
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8 py-20 grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#F5D442] to-[#E0B82C] text-[#5A4A0A] text-xs font-bold uppercase tracking-widest mb-6">
              <Crown size={12}/> Premium Listings · Featured This Quarter
            </div>
            <h1 className="font-display-tight text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.02] mb-6">
              India's most distinguished <br/>
              <span className="text-[#7CC242]">IP firms.</span>
            </h1>
            <p className="text-lg text-white/70 max-w-xl leading-relaxed mb-8">
              Hand-picked premium-tier patent attorneys and law firms — vetted for credentials, grant rates,
              and innovator-side satisfaction. The fastest, most reputed counsel in India.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/submit" className="btn-accent">Get matched with a Premium firm <ArrowRight size={14}/></Link>
              <Link to="/attorneys" className="btn-outline bg-transparent border-white/30 text-white hover:bg-white hover:text-[#0A2520]">Browse all attorneys</Link>
            </div>

            <div className="grid grid-cols-3 gap-6 mt-12 pt-8 border-t border-white/10 max-w-md">
              <Stat label="Premium firms" value={`${PREMIUM.length * 11}+`}/>
              <Stat label="Avg. rating" value="4.9"/>
              <Stat label="Median ETA" value="9 days"/>
            </div>
          </div>

          <div className="relative">
            <img src={HERO_IMAGES.lawyerA} alt="" className="rounded-3xl aspect-[4/5] object-cover w-full"/>
            <div className="absolute -bottom-6 -left-6 bg-[#7CC242] text-[#0A2520] p-5 rounded-2xl max-w-[220px]">
              <Crown size={18} className="mb-2"/>
              <div className="font-display font-bold text-lg leading-tight mb-1">Top 4% of Indian IP firms</div>
              <div className="text-xs leading-relaxed">Curated quarterly by Udhyak editorial team.</div>
            </div>
          </div>
        </div>
      </section>

      {/* Premium grid */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-20">
        <div className="flex items-end justify-between mb-10 gap-4 flex-wrap">
          <div>
            <div className="chip chip-gold mb-3"><Crown size={12}/> Premium Profiles</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0A2520]">Featured firms this quarter</h2>
          </div>
          <div className="text-sm text-[#5A6B66]">Sorted by Udhyak editorial score</div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {PREMIUM.map((p) => (
            <Link key={p.id} to={`/attorneys/${p.id}`} className="card p-0 overflow-hidden group block" data-testid={`premium-${p.id}`}>
              <div className="grid grid-cols-[140px_1fr] gap-0">
                <div className="relative">
                  <img src={p.avatar} alt={p.name} className="w-full h-full object-cover min-h-[260px]"/>
                  <div className="absolute top-3 left-3 chip-gold chip"><Crown size={10}/> Premium</div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-1 mb-2 flex-wrap">
                    {[...Array(5)].map((_, i) => <Star key={i} size={11} className="fill-[#7CC242] text-[#7CC242]"/>)}
                    <span className="text-xs font-semibold text-[#0A2520] ml-1">{p.rating} ({p.reviews})</span>
                  </div>
                  <div className="font-display text-xl font-bold text-[#0A2520] mb-1 group-hover:text-[#0E6E5E] transition">{p.name}</div>
                  <div className="text-xs text-[#5A6B66] mb-3 flex items-center gap-1"><MapPin size={11}/> {p.location}</div>
                  <p className="text-sm text-[#3D4F4A] leading-relaxed mb-4 line-clamp-3">{p.bio}</p>
                  <div className="flex gap-1.5 flex-wrap mb-4">
                    {p.specializations.map((s) => <span key={s} className="chip">{s}</span>)}
                  </div>
                  <div className="flex items-end justify-between pt-3 border-t border-[#F0F4F0]">
                    <div>
                      <div className="text-xs text-[#5A6B66]">From</div>
                      <div className="font-display text-2xl font-extrabold text-[#0A2520]">₹{p.priceFrom.toLocaleString('en-IN')}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-[#5A6B66]">{p.patentsFiled.toLocaleString()} patents</div>
                      <div className="text-xs text-[#0E6E5E] font-semibold mt-0.5">{p.turnaround}</div>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* For attorneys — become Premium */}
      <section className="surface-deep py-20 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-10"/>
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="chip chip-gold inline-flex mb-4"><Crown size={12}/> For Attorneys & Firms</div>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl text-white mb-4 leading-tight">Upgrade to <span className="text-[#7CC242]">Premium Profile</span></h2>
            <p className="text-white/70 text-lg">Stand out across India's largest IP marketplace. Premium-listed firms get more leads, faster — and a far better ROI per filing.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
            {PREMIUM_BENEFITS.map((b) => (
              <div key={b.title} className="bg-white/5 border border-white/10 rounded-2xl p-6">
                <div className="w-11 h-11 rounded-xl bg-[#7CC242] flex items-center justify-center text-[#0A2520] mb-4">
                  <b.icon size={20}/>
                </div>
                <div className="font-display font-bold text-lg text-white mb-2">{b.title}</div>
                <p className="text-sm text-white/70 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>

          {/* Pricing for attorneys */}
          <div className="grid md:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {[
              { name: "Basic Listing", price: "Free", per: "always", feat: ["Standard profile", "3 leads/month cap", "Marketplace search visibility"], cta: "Join free", highlight: false },
              { name: "Premium Profile", price: "₹14,999", per: "/ month", feat: ["Top-of-search placement", "Unlimited high-intent leads", "Editorial spotlight", "Verified Gold badge", "Priority lead routing", "Account manager"], cta: "Become Premium", highlight: true },
              { name: "Enterprise Bench", price: "Custom", per: "annual", feat: ["For 10+ partner firms", "Multi-city visibility", "API integration with your CRM", "Quarterly review with Udhyak partners", "Custom MSA"], cta: "Talk to us", highlight: false },
            ].map((t) => (
              <div key={t.name} className={`rounded-2xl p-7 border ${t.highlight ? "bg-gradient-to-b from-[#7CC242] to-[#6BAA38] border-[#7CC242] text-[#0A2520]" : "bg-white/5 border-white/10 text-white"}`}>
                {t.highlight && <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#0A2520] text-[#7CC242] text-[10px] font-bold uppercase tracking-widest mb-3"><Crown size={10}/> Recommended</div>}
                <div className="font-display font-bold text-xl mb-1">{t.name}</div>
                <div className="mb-6">
                  <span className="font-display font-extrabold text-4xl">{t.price}</span>
                  <span className={`text-sm ml-2 ${t.highlight ? "text-[#0A2520]/70" : "text-white/60"}`}>{t.per}</span>
                </div>
                <ul className="space-y-2.5 mb-7">
                  {t.feat.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check size={14} className={`flex-shrink-0 mt-0.5 ${t.highlight ? "text-[#0A2520]" : "text-[#7CC242]"}`}/> {f}
                    </li>
                  ))}
                </ul>
                <button className={`w-full justify-center ${t.highlight ? "btn-dark" : "btn-outline bg-transparent border-white/30 text-white hover:bg-white hover:text-[#0A2520]"}`}>{t.cta}</button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer/>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-display font-extrabold text-3xl text-[#7CC242] mb-1">{value}</div>
      <div className="text-xs text-white/60 uppercase tracking-widest">{label}</div>
    </div>
  );
}
